import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { request } from "../../utils/api";
import { StatisticsSkeleton } from "../Skeletons";
import { BRAND_THEME } from "../../ui/brandTheme";
import {
  STATUS_LABELS,
  STATUS_STYLES,
  PATIENT_STATUS_FILTER_ORDER,
} from "../../pages/Patients/components/StatusBadge";
import {
  UsersIcon,
  NurseIcon,
  ClockIcon,
  BriefcaseIcon,
  MedicalNotesIcon,
  HelpingIcon,
  DoctorIcon,
  UserCheckIcon,
  ArchiveIcon,
} from "../../Icons";

const STATUS_CARD_META = {
  PreAdmit: { icon: ClockIcon, hint: "Pending intake" },
  Admit: { icon: NurseIcon, hint: "Admitted census" },
  Active: { icon: UserCheckIcon, hint: "Active on service" },
  Hold: { icon: BriefcaseIcon, hint: "Temporarily on hold" },
  Inactive: { icon: ArchiveIcon, hint: "Inactive / end of care" },
  Discharged: { icon: MedicalNotesIcon, hint: "Closed stays" },
  NonAdmit: { icon: HelpingIcon, hint: "Not admitted" },
  Deceased: { icon: DoctorIcon, hint: "Deceased records" },
};

/** Overall status cards — includes Active / Inactive with badge colors. */
const STATUS_SUMMARY_CARDS = [
  ...PATIENT_STATUS_FILTER_ORDER.map((status) => ({
    status,
    icon: STATUS_CARD_META[status]?.icon || UsersIcon,
    hint: STATUS_CARD_META[status]?.hint || "",
  })),
  { status: "Total", icon: UsersIcon, hint: "All statuses", isTotal: true },
];

/**
 * Total Patient Summary — current tenant census by status.
 * Independent of From/To date filter.
 */
const PatientTotalSummary = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [statusCounts, setStatusCounts] = useState({});
  const [total, setTotal] = useState(0);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await request("/dashboard/patients/overview");
      if (res?.status === 1 && res.result) {
        setStatusCounts(res.result.statusCounts || {});
        setTotal(res.result.total ?? 0);
      } else {
        throw new Error(res?.message || "Failed to load patient summary");
      }
    } catch (err) {
      toast.error(err.message || "Failed to load patient summary");
      setStatusCounts({});
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const getCount = (status) => {
    if (status === "Total") return total;
    return statusCounts?.[status] ?? 0;
  };

  const handleCardClick = (card) => {
    if (card.isTotal) {
      navigate("/patients");
      return;
    }
    navigate(`/patients?status=${encodeURIComponent(card.status)}`);
  };

  if (loading) {
    return <StatisticsSkeleton />;
  }

  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2 px-0.5">
        <span
          className={`w-0.5 h-3.5 rounded-full ${BRAND_THEME.section.bar}`}
          aria-hidden
        />
        <h4
          className={`text-[11px] font-semibold uppercase tracking-wide ${BRAND_THEME.section.title}`}
        >
          Total patient summary
        </h4>
        <span
          className={`inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded px-1.5 text-[10px] font-semibold tabular-nums ${BRAND_THEME.section.badge}`}
        >
          {total}
        </span>
        <span className="text-[11px] text-slate-400">Current census</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {STATUS_SUMMARY_CARDS.map((card) => {
          const Icon = card.icon;
          const style = card.isTotal
            ? null
            : STATUS_STYLES[card.status] || STATUS_STYLES.Hold;
          const interactive = true;

          return (
            <div
              key={card.status}
              role="button"
              tabIndex={0}
              onClick={() => handleCardClick(card)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleCardClick(card);
                }
              }}
              className={`bg-white p-5 rounded-xl border shadow-sm transition-all duration-200 cursor-pointer hover:shadow-md group ${
                style
                  ? `${style.wrap} hover:brightness-[0.99]`
                  : `${BRAND_THEME.section.wrap} hover:border-indigo-300`
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p
                    className={`text-sm font-medium mb-1 truncate ${
                      style?.text || BRAND_THEME.section.title
                    }`}
                  >
                    {card.isTotal
                      ? "Total Patients"
                      : STATUS_LABELS[card.status] || card.status}
                  </p>
                  <h3 className="text-3xl font-bold text-slate-900 tabular-nums">
                    {getCount(card.status)}
                  </h3>
                  {card.hint ? (
                    <span className="text-xs text-slate-500 mt-1 block">
                      {card.hint}
                    </span>
                  ) : null}
                </div>
                {Icon ? (
                  <div
                    className={`p-3 rounded-lg shrink-0 ${
                      style
                        ? "bg-white/70"
                        : "bg-indigo-50 text-indigo-600"
                    } ${
                      interactive
                        ? "group-hover:scale-105 transition-transform duration-200"
                        : ""
                    }`}
                  >
                    <Icon
                      size={24}
                      className={style ? style.text : undefined}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PatientTotalSummary;
